import React, { FC, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Build as BuildIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Layers as ModuleIcon,
  ConfirmationNumber as TrialIcon,
} from '@mui/icons-material';
import { getSystemTools, SystemTool } from 'src/api2/toolsApi';
import AddToolModal from './components/AddToolModal';

const AdminToolsPage: FC = () => {
  const [tools, setTools] = useState<SystemTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState<string>('ALL');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    try {
      const fetched = await getSystemTools();
      setTools(fetched || []);
    } catch (err) {
      console.warn('Could not fetch tools from backend:', err);
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const handleToolAdded = (newTool: SystemTool) => {
    setTools((prev) => [newTool, ...prev]);
  };

  // Collect all unique module tags across all tools in the database
  const allModulesList = Array.from(
    new Set(
      tools.flatMap((t) => (t.module && t.module.length > 0 ? t.module : ['general']))
    )
  );
  const filterModuleOptions = ['ALL', ...allModulesList];

  const filteredTools = tools.filter((tool) => {
    const toolName = tool.name || tool.display_name || '';
    const toolSlug = tool.slug || tool.tool_name || '';
    const toolDesc = tool.description || '';

    const matchesSearch =
      toolName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      toolSlug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      toolDesc.toLowerCase().includes(searchQuery.toLowerCase());

    const toolModules = tool.module && tool.module.length > 0 ? tool.module : ['general'];
    const matchesModule = selectedModule === 'ALL' || toolModules.includes(selectedModule);

    return matchesSearch && matchesModule;
  });

  return (
    <Box sx={{ px: 4, py: 4, backgroundColor: '#0A0A0A', minHeight: '100vh', color: '#FFFFFF' }}>
      {/* Header Bar */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.5px' }}>
            Tools
          </Typography>
          <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px' }}>
            Register, configure, and manage platform tools
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Tooltip title="Refresh tools catalog">
            <IconButton onClick={fetchTools} sx={{ bgcolor: '#111', border: '1px solid #222', color: '#888', '&:hover': { color: '#FFF' } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddModalOpen(true)}
            sx={{
              bgcolor: '#10B981',
              color: '#000',
              fontWeight: 800,
              px: 3,
              py: 1,
              borderRadius: '10px',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            Add Tool
          </Button>
        </Box>
      </Box>

      {/* Filter and Search Bar */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search by tool name, slug, description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{
            minWidth: 300,
            bgcolor: '#111',
            borderRadius: '10px',
            '& .MuiOutlinedInput-root': {
              color: '#FFF',
              '& fieldset': { borderColor: '#222' },
              '&:hover fieldset': { borderColor: '#444' },
              '&.Mui-focused fieldset': { borderColor: '#10B981' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666' }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', py: 0.5 }}>
          {filterModuleOptions.map((mod) => (
            <Chip
              key={mod}
              label={mod}
              onClick={() => setSelectedModule(mod)}
              sx={{
                bgcolor: selectedModule === mod ? 'rgba(16, 185, 129, 0.2)' : '#111',
                color: selectedModule === mod ? '#10B981' : '#888',
                border: selectedModule === mod ? '1px solid #10B981' : '1px solid #222',
                fontWeight: 700,
                fontSize: '12px',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#FFF' },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Tools List / Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#10B981' }} />
        </Box>
      ) : filteredTools.length === 0 ? (
        <Card sx={{ bgcolor: '#111', border: '1px dashed #333', borderRadius: '16px', p: 6, textAlign: 'center' }}>
          <BuildIcon sx={{ fontSize: 48, color: '#444', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#888', fontWeight: 700, mb: 1 }}>
            No Registered Tools Found
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', mb: 3 }}>
            No tools match your criteria or no tools are currently registered in the database.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setIsAddModalOpen(true)} sx={{ color: '#10B981', borderColor: '#10B981' }}>
            Add Tool
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredTools.map((tool) => {
            const displayName = tool.name || tool.display_name || 'Unnamed Tool';
            const toolSlug = tool.slug || tool.tool_name || '';
            const modules = tool.module && tool.module.length > 0 ? tool.module : ['general'];

            return (
              <Grid item xs={12} sm={6} md={4} key={tool.tool_id}>
                <Card
                  sx={{
                    bgcolor: '#111111',
                    border: '1px solid #222222',
                    borderRadius: '16px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, border-color 0.2s',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      borderColor: '#333333',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Top Bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            p: 1.2,
                            borderRadius: '10px',
                            bgcolor: 'rgba(16, 185, 129, 0.1)',
                            color: '#10B981',
                            display: 'flex',
                          }}
                        >
                          <BuildIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem', color: '#FFF' }}>
                            {displayName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace' }}>
                            slug: {toolSlug}
                          </Typography>
                        </Box>
                      </Box>

                      <Chip
                        icon={tool.is_active ? <ActiveIcon sx={{ fontSize: '14px !important', color: '#10B981 !important' }} /> : <InactiveIcon sx={{ fontSize: '14px !important', color: '#EF4444 !important' }} />}
                        label={tool.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: tool.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          color: tool.is_active ? '#10B981' : '#EF4444',
                          border: tool.is_active ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(239, 68, 68, 0.2)',
                          fontWeight: 700,
                          fontSize: '11px',
                        }}
                      />
                    </Box>

                    {/* Description */}
                    <Typography variant="body2" sx={{ color: '#AAA', fontSize: '0.85rem', mb: 3, flexGrow: 1 }}>
                      {tool.description || 'No description provided.'}
                    </Typography>

                    {/* Footer Meta: Module Tags + Trial Limit */}
                    <Box sx={{ pt: 2, borderTop: '1px solid #1A1A1A', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <ModuleIcon sx={{ fontSize: 15, color: '#555', mr: 0.3 }} />
                        {modules.map((m) => (
                          <Chip
                            key={m}
                            label={m}
                            size="small"
                            sx={{ bgcolor: '#161616', color: '#888', fontSize: '10px', fontWeight: 600, height: 20, border: '1px solid #222' }}
                          />
                        ))}
                      </Box>

                      {tool.trial_limit !== undefined && tool.trial_limit !== null && (
                        <Tooltip title="Max trial usage limit per user">
                          <Chip
                            icon={<TrialIcon sx={{ fontSize: '12px !important', color: '#F59E0B !important' }} />}
                            label={`Trial: ${tool.trial_limit}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B', border: '1px solid rgba(245, 158, 11, 0.2)', fontSize: '10px', fontWeight: 700, height: 20 }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Modal for adding new tool */}
      <AddToolModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onToolAdded={handleToolAdded} />
    </Box>
  );
};

export default AdminToolsPage;
